/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/

import TargetMap from '../actions/targetmap';
import constants, { publicConstants } from '../constants';
import {
  CONTAINER_NAMES,
  getContainerItemData,
  getDataContextName,
  getTargetFromContainerItemID,
  getContainerData,
  getActiveContainerItemName
} from '../container/container-utils';
import * as actions from '../store/state/actions/action-creators';
import { getParentContextName, getTargetFromContext } from '../utils/context-utils';
import EnvironmentInfo from '../environment-info';
import Utils from '../utils/utils';
import { didOperatorHasAssignment, isAssignmentInCreateStage } from '../utils/case-utils';
import { ACTIVATE_CONTAINER_ITEM, ERROR, ACTION_OPENWORKBYHANDLE } from '../store/state/actions';
import * as StateManager from '../store/state/state-manager';
import { ActionManagerInstance } from '../actions/manager';
import type { Action } from '../store/state/state-manager';
import { getObjectInfo } from '../object/common-helper';
import { getSemanticURL, parseResponse } from '../router/app-router';

import { publishCaseAssignmentIDUpdateEvent } from './common-events';
import type { DxAPIResponseObject, ActionPayloadDataObj } from './types';

export const DXAPI_RESPONSE_DATA_READ_PATHS = {
  CASE_INFO: `${constants.DATA}.${constants.CASE_INFO.CASE_INFO}`,
  CASE_CLASS_NAME: `${constants.DATA}.${constants.CASE_INFO.CASE_TYPE_ID}`,
  NEXT_ASSIGNMENT_INFO_ID: `${constants.DATA}.${constants.NEXT_ASSIGNMENT_INFO_ID}`,
  ASSIGNMENTS: `${constants.DATA}.${constants.CASE_INFO.ASSIGNMENTS}`,
  CASE_INFO_ID: `${constants.DATA}.${constants.CASE_INFO.CASE_INFO_ID}`,
  CASE_FIRST_FLOW_ACTION_NAME: `${constants.DATA}.${constants.CASE_INFO.ASSIGNMENTACTION_ID}`,
  CASE_FIRST_ASSIGNMENT_ID: `${constants.DATA}.${constants.CASE_INFO.ASSIGNMENT_ID}`
};

export const getContainerName = (actionType: Action['type'], actionInPayload: Action['payload']) => {
  const { vcName, containerName } = actionInPayload;
  /* [TODO]: we shouldn't use "vcName" anywhere, setting this value...since app-router uses this logic */
  return containerName || vcName || TargetMap.getTargetViewContainerName(actionType);
};

export const getContainerNameFromContainerItemID = (containerItemID: string) => {
  const startIndex = containerItemID.lastIndexOf(constants.FORWARD_SLASH_STRING) + 1;
  const endIndex = containerItemID.lastIndexOf(constants.UNDERSCORE_STRING);
  return containerItemID.substring(startIndex, endIndex);
};

export const isWorkAreaContainer = (containerName: string) => {
  return containerName === CONTAINER_NAMES.WORKAREA;
};

export const isModalContainer = (containerName: string) => {
  return containerName === CONTAINER_NAMES.MODAL;
};

export const isActivateContainerItemActionType = (actionType: Action['type']) => {
  return actionType === ACTIVATE_CONTAINER_ITEM;
};

export const getNextAssignmentInfoID = (dxApiResponse: DxAPIResponseObject) => {
  return Utils.getIn(dxApiResponse, DXAPI_RESPONSE_DATA_READ_PATHS.NEXT_ASSIGNMENT_INFO_ID, constants.EMPTY_STRING);
};

export const isErrorActionType = (actionType: Action['type']) => {
  return actionType === ERROR;
};

export const hasErrorActionType = (actionsOut: Action[]) => {
  return actionsOut && actionsOut.find((obj) => obj.type === ERROR);
};

export const getCaseInfo = (dxApiResponse: DxAPIResponseObject) => {
  return Utils.getIn(dxApiResponse, DXAPI_RESPONSE_DATA_READ_PATHS.CASE_INFO, {});
};

export const hasAssignments = (dxApiResponse: DxAPIResponseObject) => {
  const assignments = Utils.getIn(dxApiResponse, DXAPI_RESPONSE_DATA_READ_PATHS.ASSIGNMENTS, []);
  return assignments && assignments.length > 0;
};

export const getDisplayCaseID = (caseID?: string) => {
  return caseID ? caseID.split(' ').pop() : undefined;
};

export const getCaseClassName = (dxApiResponse: DxAPIResponseObject) => {
  return Utils.getIn(dxApiResponse, DXAPI_RESPONSE_DATA_READ_PATHS.CASE_CLASS_NAME, constants.EMPTY_STRING);
};

export const isNextStepInCreateStage = (dxApiResponse: DxAPIResponseObject, isObjectAction = false) => {
  const caseInfo = isObjectAction ? getObjectInfo(dxApiResponse) : getCaseInfo(dxApiResponse);
  return (
    isAssignmentInCreateStage(caseInfo) && didOperatorHasAssignment(EnvironmentInfo.getOperatorIdentifier(), caseInfo)
  );
};

export const getCaseInfoID = (dxApiResponse: DxAPIResponseObject) => {
  return Utils.getIn(dxApiResponse, DXAPI_RESPONSE_DATA_READ_PATHS.CASE_INFO_ID, constants.EMPTY_STRING);
};

export const getCaseFirstFlowActionID = (dxApiResponse: DxAPIResponseObject) => {
  return Utils.getIn(dxApiResponse, DXAPI_RESPONSE_DATA_READ_PATHS.CASE_FIRST_FLOW_ACTION_NAME, constants.EMPTY_STRING);
};

export const getCaseFirstAssignmentID = (dxApiResponse: DxAPIResponseObject) => {
  return Utils.getIn(dxApiResponse, DXAPI_RESPONSE_DATA_READ_PATHS.CASE_FIRST_ASSIGNMENT_ID, constants.EMPTY_STRING);
};

export const addActionToRemoveContainerItem = (
  actionsOut: Action[],
  options: { removeActionPayload: Action['payload']; target: string | null }
) => {
  const { removeActionPayload, target } = options;
  /* action to remove previous container */
  if (removeActionPayload && removeActionPayload.containerItemID) {
    const { containerItemID, context } = removeActionPayload;
    actionsOut.push(
      actions.postProcess(actions.removeContainerItem, {
        containerItemID,
        context: context || getDataContextName(containerItemID),
        target
      })
    );
  }
};

/**
 * this function helps to check whether parent and child container data context referring to same work item or not
 * @private
 * @param childContainerItemID - child container item id
 * @returns - true --> if both container data context refers to same work item
 */
export const isParentAndChildContextHoldingSameItem = (childContainerItemID: string | null): boolean => {
  const state = StateManager.getStore();

  // return if childContainerItemID is not present
  if (!childContainerItemID) return false;

  const parentContainerItemID = getParentContextName(childContainerItemID);
  const parentTarget = getTargetFromContainerItemID(parentContainerItemID);
  const childTarget = getTargetFromContainerItemID(childContainerItemID);

  // return false if parent target or child target are nullish values
  if (!parentTarget || !childTarget) return false;

  const parentItemData = getContainerItemData(parentTarget, parentContainerItemID);
  const childItemData = getContainerItemData(childTarget, childContainerItemID);

  // return false if parent item or child item data are not present
  if (!parentItemData || !childItemData) return false;

  const { key: parentKey } = parentItemData || {};
  let { key: childKey } = childItemData || {};
  if (!childKey) {
    childKey = state?.getState().data?.[childContainerItemID]?.caseInfo?.ID;
  }
  return parentKey && childKey && parentKey === childKey;
};

/**
 * Checks whether parent and child container data contexts hold confirmed DIFFERENT work items.
 * Unlike isParentAndChildContextHoldingSameItem, this returns false (not different) when
 * container data is unavailable — avoiding false positives in partially-loaded state.
 * @private
 * @param childContainerItemID - child container item id
 * @returns - true only when both contexts are loaded and confirmed to hold different items
 */
export const isParentAndChildContextHoldingDifferentItem = (childContainerItemID: string | null): boolean => {
  const state = StateManager.getStore();

  if (!childContainerItemID) return false;

  const parentContainerItemID = getParentContextName(childContainerItemID);
  const parentTarget = getTargetFromContainerItemID(parentContainerItemID);
  const childTarget = getTargetFromContainerItemID(childContainerItemID);

  if (!parentTarget || !childTarget) return false;

  const parentItemData = getContainerItemData(parentTarget, parentContainerItemID);
  const childItemData = getContainerItemData(childTarget, childContainerItemID);

  // When data is not loaded, we cannot confirm items are different — do not dispatch
  if (!parentItemData || !childItemData) return false;

  const { key: parentKey } = parentItemData;
  let { key: childKey } = childItemData;
  if (!childKey) {
    childKey = state?.getState().data?.[childContainerItemID]?.caseInfo?.ID;
  }

  // Only true when both keys are present AND confirmed different
  return !!(parentKey && childKey && parentKey !== childKey);
};

export const updateKeyAndFlowName = (
  sourceObject: object,
  dxApiResponse: DxAPIResponseObject,
  options: { skipFlowNameUpdate?: boolean } = {}
) => {
  /* flowName is not required when case is in review mode */
  const { skipFlowNameUpdate } = options;

  const caseInfoID = getCaseInfoID(dxApiResponse);
  Utils.setIn(sourceObject, 'key', caseInfoID);

  const isInCreateStage = isAssignmentInCreateStage(getCaseInfo(dxApiResponse));

  if (!skipFlowNameUpdate && hasAssignments(dxApiResponse)) {
    const assignmentID = getCaseFirstAssignmentID(dxApiResponse);
    const [, flowName] = assignmentID.split('!');
    publishCaseAssignmentIDUpdateEvent({ assignmentID, isInCreateStage });
    Utils.setIn(sourceObject, 'flowName', flowName);
  }
};

/**
 * this function helps to set last submit time property in context_data
 * @param data - data object to which refresh properties will get added
 * @param actionInPayload -  ncoming action payload from public action api
 * @private
 */
export const setLastRefreshTimeProperty = (data: ActionPayloadDataObj, actionInPayload: Action['payload']) => {
  const { attachmentsExist } = actionInPayload;
  if (attachmentsExist) {
    Utils.setIn(data, publicConstants.SUMMARY_OF_ATTACHMENTS_LAST_REFRESH_TIME, new Date().toLocaleString());
  }

  Utils.setIn(data, publicConstants.SUMMARY_OF_ASSIGNMENT_LAST_SUBMIT_TIME, new Date().toLocaleString());
};

/**
 * this function handles the promise logic
 * @private
 * @param actionsOut - actions which will get dispatched out
 * @param actionMgrID - action manager id
 * @param errorDetails - that gives the type of information about the error
 * @returns - returns 'reject' or 'resolve'
 */
export const promiseHandler = (actionsOut: Action[], actionMgrID: number, errorDetails?: object) => {
  let promiseValue = constants.PROMISE_VALUE.RESOLVE;
  if (hasErrorActionType(actionsOut)) {
    promiseValue = constants.PROMISE_VALUE.REJECT;
    if (errorDetails) {
      ActionManagerInstance.rejectActionByID(actionMgrID, true, errorDetails);
    } else {
      ActionManagerInstance.rejectActionByID(actionMgrID);
    }
  } else {
    ActionManagerInstance.resolveActionByID(actionMgrID);
  }
  return promiseValue;
};

/**
 * this function tests whether given case is coexistence or not
 * @private
 * @param className - classname of the case; when undefined, returns false immediately
 * @returns - returns boolean if case is coexistence or not
 */
export const isCoexistenceCase = (className: string | undefined) => {
  if (!className) {
    return false;
  }
  const caseList = EnvironmentInfo.getCaseTypeList();

  return (
    caseList.filter((item) => {
      return item.pyWorkTypeImplementationClassName === className;
    })?.[0]?.pyIsTraditionalUICase === 'true'
  );
};

/**
 * returns flag whether the app built on Theme Cosmos or not
 * @public
 * @static
 */
export const canLaunchCaseInModal = () =>
  EnvironmentInfo?.environmentInfoObject?.pxMashupDetails?.pyLaunchCaseInModal === 'true';

export const addActionToLoadCaseSummaryView = (
  actionsOut: Action[],
  actionInPayload: Action['payload'],
  caseData: DxAPIResponseObject,
  options: { caseClassName: string; skipAssignmentsUpdate?: boolean }
) => {
  const { containerItemID } = actionInPayload;
  if (containerItemID) {
    const { caseClassName, skipAssignmentsUpdate } = options;
    const parentContext = getParentContextName(containerItemID);
    const parentTarget = getTargetFromContainerItemID(parentContext);
    const parentContainersData = parentTarget && getContainerData(parentTarget);
    const activeContainerItem = parentTarget && getActiveContainerItemName(parentTarget);
    const activeContainerName = getTargetFromContext(parentContext);
    const caseID = caseData?.data?.caseInfo?.ID;
    const isContainerActive =
      activeContainerItem && parentContainersData?.items?.[activeContainerItem]?.key === actionInPayload?.caseID;
    if (isContainerActive) {
      actionsOut.push(
        actions.postProcess(actions.loadView, {
          containerItemID: parentContext,
          context: parentContext,
          container: activeContainerName,
          containerName: activeContainerName,
          caseId: caseID,
          viewName: publicConstants.CASESUMMARY_VIEW_NAME,
          updateData: true,
          viewLoadingContainer: {
            context: parentContext
          },
          caseClassName,
          ...(skipAssignmentsUpdate !== undefined && { skipAssignmentsUpdate })
        })
      );
    }
  }
};

export const deferCaseSummaryViewUpdate = (
  chainedData: { response: DxAPIResponseObject; actionInPayload: Action['payload'] },
  actionsOut: Action[]
) => {
  const caseClassName = getCaseClassName(chainedData?.response);
  const caseInfo = getCaseInfo(chainedData?.response);
  const { caseTypeID } = caseInfo;
  const featureMap: { deferUpdateCaseSummaryView: boolean } = EnvironmentInfo.getFeatureMap() as {
    deferUpdateCaseSummaryView: boolean;
  };
  const deferUpdateCaseSummaryView =
    featureMap?.deferUpdateCaseSummaryView ?? !Object.hasOwn(featureMap, constants.DEFER_UPDATE_CASESUMMARY_VIEW);
  if (deferUpdateCaseSummaryView && caseTypeID) {
    addActionToLoadCaseSummaryView(actionsOut, chainedData?.actionInPayload, chainedData?.response, {
      caseClassName,
      skipAssignmentsUpdate: !chainedData?.actionInPayload?.context?.includes(constants.PREVIEW)
    });
  }
};

export const isPageGroupEnabled = () => {
  const featureMap: { pyEnablePageGroup: boolean } = EnvironmentInfo.getFeatureMap() as {
    pyEnablePageGroup: boolean;
  };
  return featureMap?.pyEnablePageGroup ?? false;
};

export const getCurrentCaseViewMode = (context: string): string => {
  return StateManager.getDataObject(context)?.context_data?.caseViewMode;
};

export const resetSemanticURLFromAssignmentToCase = (containerItemID: string, response: DxAPIResponseObject) => {
  const caseID = getCaseInfoID(response);

  let semanticURL = getSemanticURL(
    ACTION_OPENWORKBYHANDLE,
    { caseClassName: getCaseClassName(response) },
    { id: caseID }
  );
  semanticURL = semanticURL
    ?.replace('{workID}', String(getDisplayCaseID(caseID)))
    .replace('{id}', String(getDisplayCaseID(caseID)));

  parseResponse({
    directUrlUpdation: true,
    semanticURL,
    options: {}
  });
};
